import React, { useEffect, useState } from 'react';
import { SubmarkLogo } from './Icons';
import { trackClick } from '../../utils/analytics';
import { HOMEPAGE_DEMO_BOOKING_URL } from '../../config/booking';

const BANNER_LINK = "https://bBzur8.short.gy/rnddubai";
const BANNER_IMG = "/Banner/16_1.png";
const BANNER_ALT = "R&D Symposium 2026 - 22-24 April, Dubai";

interface HeaderProps {
  onLoginClick: () => void;
  onRequestInviteClick: () => void;
  onSignUpClick?: () => void;
  onSupportClick: () => void;
  showBanner?: boolean;
}

/** Returns current window width, updates on resize */
function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

/**
 * Infinite horizontal scrolling banner.
 * Uses 4 copies so the seam is never visible — translate by -25% (one copy).
 * Duration is responsive: faster on mobile (smaller image, less to traverse).
 */
const COPIES = 4;

const ScrollingBanner: React.FC<{ className?: string }> = ({ className = '' }) => {
  const viewportWidth = useWindowWidth();

  // Each copy is at least 1100px or the full viewport, whichever is larger
  const copyWidth = Math.max(viewportWidth, 1100);

  const duration =
    viewportWidth < 480  ? 10
    : viewportWidth < 768  ? 12
    : viewportWidth < 1024 ? 14
    : viewportWidth < 1440 ? 16
    : 18;

  const imgStyle: React.CSSProperties = {
    flexShrink: 0,
    display: 'block',
    height: 'auto',
    verticalAlign: 'top',
    minWidth: `${copyWidth}px`,   // ← exact pixel width, no ambiguity
  };

  return (
    <div
      className={`w-full overflow-hidden ${className}`}
      style={{ margin: 0, padding: 0, lineHeight: 0, fontSize: 0 }}
    >
      <div
        style={{
          display: 'flex',
          margin: 0,
          padding: 0,
          animation: `bannerScroll ${duration}s linear infinite`,
          willChange: 'transform',
        }}
      >
        {Array.from({ length: COPIES }).map((_, i) => (
          <img
            key={i}
            src={BANNER_IMG}
            alt={i === 0 ? BANNER_ALT : ''}
            aria-hidden={i !== 0}
            style={imgStyle}
            draggable={false}
          />
        ))}
      </div>

      {/* Pixel-based translate = exactly one copy width, always */}
      <style>{`
        @keyframes bannerScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-${copyWidth}px); }
        }
      `}</style>
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({
  onLoginClick,
  onRequestInviteClick,
  onSignUpClick,
  onSupportClick,
  showBanner = false,
}) => {
  return (
    <header className="w-full overflow-x-hidden">
      {showBanner && (
        <a href={BANNER_LINK}
        target="_blank"
        rel="noopener noreferrer"
        style={{ margin: 0, padding: 0, lineHeight: 0, fontSize: 0, display: 'block' }}
      >
        <ScrollingBanner />
      </a>
      )}

      <div className="flex justify-between items-center flex-wrap gap-2 sm:flex-nowrap py-3 sm:py-4">
        <div className="flex-shrink-0" onClick={() => trackClick('pre_login_click_logo')} style={{ cursor: 'pointer' }}>
          <SubmarkLogo />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-wrap sm:flex-nowrap min-w-0">
          <button
            onClick={() => { trackClick('pre_login_click_login'); onLoginClick(); }}
            className="px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-button-text bg-primary-600 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap flex-shrink-0">
            Login
          </button>
          {onSignUpClick && (
            <button
              onClick={() => { trackClick('pre_login_click_signup'); onSignUpClick(); }}
              className="px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-button-text bg-primary-600 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap flex-shrink-0">
              Sign Up
            </button>
          )}
          {/* Outlined rather than filled: it sits beside two solid buttons and
              a third would leave nothing looking like the primary action.
              Google's booking page refuses to be framed, so it opens in a tab. */}
          <a
            href={HOMEPAGE_DEMO_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('pre_login_click_book_demo')}
            className="px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-primary-700 bg-grayscale-100 border border-primary-600 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap flex-shrink-0">
            <span className="hidden sm:inline">Book a Demo</span>
            <span className="sm:hidden">Demo</span>
          </a>
          <button
            onClick={() => { trackClick('pre_login_click_help'); onSupportClick(); }}
            className="px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 text-[10px] sm:text-xs md:text-sm font-medium text-main-text bg-grayscale-100 border border-primary-200 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap flex-shrink-0"
          >
            <span className="hidden sm:inline">Need Assistance ?</span>
            <span className="sm:hidden">Help ?</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;